/**
 * Filter `type` value used by the workplace rating-prompt Knock guide. Authors of
 * the guide in the Knock dashboard set this as the guide type so `useGuides`
 * matches it from `ReviewPromptGuide`. Other Knock surfaces (`banner`, `modal`)
 * are untouched.
 */
export const REVIEW_PROMPT_GUIDE_TYPE = "workplace-app-store-review-soft-prompt";

/**
 * Destination for the feedback-step "Contact support" button. Opened in the
 * Capacitor in-app browser on native and a new tab on web; the dialog stays
 * mounted behind it so the user can still finish the prompt afterward.
 */
export const REVIEW_PROMPT_SUPPORT_URL =
  "https://support.clipboardhealth.com/hc/en-us/requests/new";

/**
 * Knock user attribute that holds the ISO timestamp of the last time the
 * rating prompt's native review sheet was *requested*. Read by the Knock
 * dashboard's `daysSinceLastReviewPromptedAt` targeting rule so the gap
 * between prompts is dashboard-tunable (D-04).
 *
 * Caveat: the Capacitor plugin resolves whether the OS actually displayed
 * the sheet or silently rate-limited it (Apple caps to 3 per app per 365d;
 * Play also throttles). We persist the timestamp on every request, so a
 * rate-limited prompt counts the same as a shown prompt for cooldown.
 */
export const KNOCK_LAST_REVIEW_PROMPTED_AT_ATTRIBUTE = "lastReviewPromptedAt";

/**
 * Knock user attribute holding the ISO timestamp of the user's first session
 * on a Capacitor-native build of the app (iOS or Android). Read by the Knock
 * dashboard's `daysSince(firstMobileAppUseAt)` targeting rule to enforce a
 * "soak time on the mobile app" floor before any rating prompt fires —
 * stricter than Cognito tenure, which Knock can derive from its built-in
 * `recipient.createdAt`.
 *
 * Written exactly once per user-account: `useEmitReviewPromptAttributes`
 * reads the current value via `user.get()` and only writes the timestamp
 * when it is absent. This avoids a cross-device replay where a later mobile
 * install would otherwise overwrite the earliest one (Knock identify is
 * last-write-wins; there is no min-merge primitive). The Knock value
 * becomes "earliest mobile install we observed across any device."
 */
export const KNOCK_FIRST_MOBILE_APP_USE_AT_ATTRIBUTE = "firstMobileAppUseAt";

/**
 * Knock user attribute that holds the ISO timestamp of the last time the user
 * declined the rating prompt (tapped No on the mood step). Primary dashboard
 * gate for the decline path: the initial targeting rule shows the prompt only
 * when this attribute is undefined. Can later be flipped to an interval check
 * ("now − lastDeclinedReviewPromptAt > N days") in the dashboard without a
 * code change. Written once per decline on the No tap; Submit and Cancel
 * don't re-write.
 */
export const KNOCK_LAST_DECLINED_REVIEW_PROMPT_AT_ATTRIBUTE = "lastDeclinedReviewPromptAt";

/**
 * Knock user attribute that holds the ISO timestamp of the last time the user
 * dismissed the rating prompt with "Ask me later". Retained as a historical
 * signal for analytics; the active cooldown gate is `reviewPromptNextEligibleAt`.
 */
export const KNOCK_LAST_DISMISSED_REVIEW_PROMPT_AT_ATTRIBUTE = "lastDismissedReviewPromptAt";

/**
 * Knock user attribute counting how many times the user has tapped "Ask me
 * later" on the mood step. Used client-side to compute the next backoff step
 * — the Knock dashboard does not need to read this; it reads
 * `reviewPromptNextEligibleAt` only. Starts undefined and increments by one
 * on each dismissal. Not touched by the decline path.
 */
export const KNOCK_REVIEW_PROMPT_DISMISS_COUNT_ATTRIBUTE = "reviewPromptDismissCount";

/**
 * Knock user attribute counting how many times the user has tapped "Not really"
 * on the mood step (declined the rating prompt). Surfaced for the Knock
 * dashboard's targeting rule — the prompt's eligibility check can read this
 * directly to enforce policies like "never re-prompt after one No" or
 * "permanently exclude after N declines." Starts undefined and increments by
 * one on each decline. Incremented only on the No tap itself, not on the
 * Submit/Cancel idempotent cooldown re-writes.
 */
export const KNOCK_REVIEW_PROMPT_DECLINE_COUNT_ATTRIBUTE = "reviewPromptDeclineCount";

/**
 * Knock user attribute holding the ISO timestamp of the earliest moment the
 * rating prompt may re-fire for this user as part of the **dismiss cooldown**
 * (Ask me later). Written only on the dismiss path by
 * `REVIEW_PROMPT_DISMISS_BACKOFF_DAYS`. The decline path doesn't touch this
 * attribute — it gates off `lastDeclinedReviewPromptAt` instead — so this
 * stays a clean single-purpose signal the dashboard can compare with `now`.
 * The stepped backoff schedule lives in code because the dashboard editor
 * only supports one nesting layer per rule.
 */
export const KNOCK_REVIEW_PROMPT_NEXT_ELIGIBLE_AT_ATTRIBUTE = "reviewPromptNextEligibleAt";

/**
 * Step-based backoff applied when the user taps "Ask me later". Indexed by
 * the current dismiss count: first dismissal pushes the prompt out 7 days,
 * second 14, third (and any subsequent) 30. Lives in code because the
 * dashboard rule reads only `reviewPromptNextEligibleAt` — see
 * `KNOCK_REVIEW_PROMPT_NEXT_ELIGIBLE_AT_ATTRIBUTE`.
 */
export const REVIEW_PROMPT_DISMISS_BACKOFF_DAYS = [7, 14, 30] as const;
